import * as React from 'react';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector, einheitenByCodeSelector } from 'store/lookups/selector';
import { AngebotPosition } from 'api/angebotPosition';
import classnames from 'classnames';
import { formatDecimal } from 'utils/formatter';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { LieferArt } from 'formComponents/lookups/LieferArt';
import { formStyles } from 'styles/formStyles';
import { FormDecimalField } from 'formComponents/FormDecimalField';

interface AngebotPositionenProps {
    angebotPositionen: AngebotPosition[];
    onChange: (newTransferPositionen: AngebotPosition[]) => void;
    withLArt: boolean;
}

export const AngebotPositionenSelect: React.FC<AngebotPositionenProps> = ({
    angebotPositionen,
    onChange,
    withLArt,
}) => {
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);

    return (
        <div>
            <h3>Bitte wählen Sie die zu übernehmenden Positionen und Mengen</h3>
            <table className={formStyles.positionenTable}>
                <thead className={formStyles.positionenTable}>
                    <tr>
                        <td />
                        <td className={formStyles.positionenNrCell}>Pos.</td>
                        <td className={formStyles.positionenArtikelGruppeCell}>Art. Grp.</td>
                        <td className={formStyles.positionenArtikelNrCell}>Art. Nr.</td>
                        <td className={formStyles.positionenTextCell}>Text</td>
                        <td className={formStyles.positionenEinhCell}>Einh</td>
                        <td className={formStyles.positionenMoneyCell}>Menge</td>
                        <td className={formStyles.transferMengeHeader}>
                            Menge
                            <br />
                            übernehmen
                        </td>
                        {withLArt && <td className={formStyles.transferLArt}>LArt</td>}
                    </tr>
                </thead>
                <tbody>
                    {angebotPositionen.map((angebotPosition) => (
                        <tr key={angebotPosition.pos} className={formStyles.positionenRow}>
                            <td>
                                <Checkbox
                                    className={formStyles.checkbox}
                                    checked={angebotPosition.transfer}
                                    onChange={(event, checked) => {
                                        onChange(
                                            angebotPositionen.map((ap) =>
                                                ap.pos === angebotPosition.pos
                                                    ? {
                                                          ...angebotPosition,
                                                          transfer: checked,
                                                      }
                                                    : ap,
                                            ),
                                        );
                                    }}
                                />
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenNrCell)}>
                                {angebotPosition.pos}
                            </td>
                            <td
                                className={classnames(
                                    formStyles.positionenCell,
                                    formStyles.positionenArtikelGruppeCell,
                                )}
                            >
                                {(artikelGruppenByNr[angebotPosition.artikelGruppeNr] &&
                                    artikelGruppenByNr[angebotPosition.artikelGruppeNr]?.text) ||
                                    angebotPosition.artikelGruppeNr}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenArtikelNrCell)}>
                                <span>{angebotPosition.artikelNr}</span>
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenTextCell)}>
                                {angebotPosition.posText}
                                {angebotPosition.posBem ? (
                                    <i>
                                        <br />
                                        {angebotPosition.posBem}
                                    </i>
                                ) : (
                                    ''
                                )}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenEinhCell)}>
                                {angebotPosition.posEinh
                                    ? einheitenByCode[angebotPosition.posEinh].text || angebotPosition.posEinh
                                    : '?'}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMengeCell)}>
                                {formatDecimal(angebotPosition.posMenge, 3)}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                <FormDecimalField
                                    options={{
                                        disabled: !angebotPosition.transfer,
                                        fixed: 3,
                                    }}
                                    label=""
                                    value={angebotPosition.transferMenge || 0}
                                    onChange={(newValue): void => {
                                        onChange(
                                            angebotPositionen.map((ap) =>
                                                ap.pos === angebotPosition.pos
                                                    ? {
                                                          ...angebotPosition,
                                                          transferMenge: newValue,
                                                      }
                                                    : ap,
                                            ),
                                        );
                                    }}
                                />
                            </td>
                            {withLArt && (
                                <td className={classnames(formStyles.positionenCell, formStyles.transferLArt)}>
                                    <LieferArt
                                        allowEmpty
                                        disabled={!angebotPosition.transfer}
                                        value={(angebotPosition?.transferLArt as string) || ''}
                                        menuPlacement="top"
                                        renderLabel="no"
                                        onChange={(newValue) => {
                                            onChange(
                                                angebotPositionen.map((ap) =>
                                                    ap.pos === angebotPosition.pos
                                                        ? {
                                                              ...angebotPosition,
                                                              transferLArt: newValue,
                                                          }
                                                        : ap,
                                                ),
                                            );
                                        }}
                                    />
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
